import { ofType } from 'redux-observable'
import { defer, of } from 'rxjs'
import { catchError, filter, groupBy, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { combineEpicsIsolated, reportEpicCrash } from 'utils/epicRegistry'
import { COMMON_ERROR, isError } from 'utils/ErrorMessages'

import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import {
  CLEAR_WIZARD_ACTION,
  DISABLE_PSA_INTEGRATION_ASYNC,
  GET_PROVIDER_CONFIG_ASYNC,
  GET_PSA_TILE_ASYNC,
  GET_WIZARD_SCHEMA_ASYNC,
  RUN_WIZARD_ACTION_ASYNC,
  SAVE_PSA_INTEGRATION_ASYNC,
} from './constants'
import { selectPSAWizardSchema } from './selectors'

const EXPECTED_ERRORS = [400, 403, 404, 429, 502, 504]

const companyFilterOf = (state$) => selectCompanyFilter(state$.value)?.value || ''

// epicsErrorHandler rethrows a thrown Error (such as a failed CSRF token refresh), which would
// leave the request pending for good; the wizard locks itself while a save or a test ticket waits
// and the tile shows a spinner while it loads or removes, so every failure must answer the request
// it belongs to. Caught here, the Error never reaches the
// root epic's crash report, so it is reported here instead.
const failureOf = (epicsErrorHandler, onError, error, label) => {
  if (!isError(error)) return epicsErrorHandler(onError, error, EXPECTED_ERRORS)
  reportEpicCrash(label, error)
  return onError({ title: 'Error', detail: COMMON_ERROR, expectedErrorCodes: EXPECTED_ERRORS })
}

// rxfetch's post and ajax resolve to the AjaxResponse; get resolves to the body.
const bodyOf = (ajaxResponse) => ajaxResponse?.response ?? null

export const getWizardSchemaEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_WIZARD_SCHEMA_ASYNC.INITIATE),
    switchMap(({ payload }) => {
      const { providerKey } = payload
      const cached = selectPSAWizardSchema(state$.value, providerKey)
      if (cached) return of(GET_WIZARD_SCHEMA_ASYNC.onSuccess({ providerKey, manifest: cached }))
      return api.psaIntegrations.getWizardSchema(providerKey, companyFilterOf(state$)).pipe(
        map((manifest) => GET_WIZARD_SCHEMA_ASYNC.onSuccess({ providerKey, manifest })),
        catchError((error) =>
          of(epicsErrorHandler(GET_WIZARD_SCHEMA_ASYNC.onError, error, EXPECTED_ERRORS))
        )
      )
    })
  )

export const getProviderConfigEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_PROVIDER_CONFIG_ASYNC.INITIATE),
    switchMap(({ payload }) =>
      api.psaIntegrations.getIntegrationConfig(payload.providerKey, companyFilterOf(state$)).pipe(
        map((config) => GET_PROVIDER_CONFIG_ASYNC.onSuccess(config)),
        catchError((error) =>
          of(epicsErrorHandler(GET_PROVIDER_CONFIG_ASYNC.onError, error, EXPECTED_ERRORS))
        )
      )
    )
  )

// The request body can hold the plain client secret, so an emitted action never copies it.
export const runWizardActionEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RUN_WIZARD_ACTION_ASYNC.INITIATE),
    mergeMap(({ payload }) => {
      const { providerKey, actionType, requestId, payload: body = {} } = payload
      return defer(() =>
        api.psaIntegrations.runWizardAction(providerKey, actionType, body, companyFilterOf(state$))
      ).pipe(
        map((ajaxResponse) =>
          RUN_WIZARD_ACTION_ASYNC.onSuccess({
            requestId,
            actionType,
            response: bodyOf(ajaxResponse),
          })
        ),
        catchError((error) =>
          of(
            failureOf(
              epicsErrorHandler,
              (info) => RUN_WIZARD_ACTION_ASYNC.onError({ ...info, requestId, actionType }),
              error,
              'PsaWizard:runWizardActionEpic'
            )
          )
        ),
        takeUntil(
          action$.pipe(
            ofType(CLEAR_WIZARD_ACTION),
            filter((clear) => clear.payload?.requestId === requestId)
          )
        )
      )
    })
  )

export const savePsaIntegrationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_PSA_INTEGRATION_ASYNC.INITIATE),
    mergeMap(({ payload }) => {
      const { providerKey, requestId, payload: body } = payload
      // A save can fail on its way back after the server made the integration live, so the tile
      // is reloaded after every answer and shows what the server holds.
      return defer(() =>
        api.psaIntegrations.saveIntegration(providerKey, body, companyFilterOf(state$))
      ).pipe(
        map((ajaxResponse) =>
          SAVE_PSA_INTEGRATION_ASYNC.onSuccess({ requestId, response: bodyOf(ajaxResponse) })
        ),
        catchError((error) =>
          of(
            failureOf(
              epicsErrorHandler,
              (info) => SAVE_PSA_INTEGRATION_ASYNC.onError({ ...info, requestId }),
              error,
              'PsaWizard:savePsaIntegrationEpic'
            )
          )
        ),
        mergeMap((answer) => of(answer, GET_PSA_TILE_ASYNC.initiate({ providerKey })))
      )
    })
  )

// Per provider, a newer load replaces the one in flight, and an answer for a company the partner
// has since left is dropped, so a tile only ever shows the selected company's state.
export const getPsaTileEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_PSA_TILE_ASYNC.INITIATE),
    groupBy(({ payload }) => payload.providerKey),
    mergeMap((provider$) =>
      provider$.pipe(
        switchMap(({ payload }) => {
          const { providerKey } = payload
          const company = companyFilterOf(state$)
          return api.psaIntegrations.getIntegrationStatus(providerKey, company).pipe(
            map((config) => GET_PSA_TILE_ASYNC.onSuccess({ providerKey, config })),
            catchError((error) =>
              of(
                failureOf(
                  epicsErrorHandler,
                  (info) => GET_PSA_TILE_ASYNC.onError({ ...info, providerKey }),
                  error,
                  'PsaWizard:getPsaTileEpic'
                )
              )
            ),
            filter(() => companyFilterOf(state$) === company)
          )
        })
      )
    )
  )

// The confirm closes on Remove, so the partner can move to another company before the answer; an
// answer for a company the partner has left is dropped, as the tile load drops its own. A disable
// can fail on its way back after psa-service removed the setup, so the tile is reloaded after
// every answer, as after a save.
export const disablePsaIntegrationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DISABLE_PSA_INTEGRATION_ASYNC.INITIATE),
    mergeMap(({ payload }) => {
      const { providerKey } = payload
      const company = companyFilterOf(state$)
      return api.psaIntegrations.disableIntegration(providerKey, company).pipe(
        map(() => DISABLE_PSA_INTEGRATION_ASYNC.onSuccess({ providerKey })),
        catchError((error) =>
          of(
            failureOf(
              epicsErrorHandler,
              (info) => DISABLE_PSA_INTEGRATION_ASYNC.onError({ ...info, providerKey }),
              error,
              'PsaWizard:disablePsaIntegrationEpic'
            )
          )
        ),
        filter(() => companyFilterOf(state$) === company),
        mergeMap((answer) => of(answer, GET_PSA_TILE_ASYNC.initiate({ providerKey })))
      )
    })
  )

export default combineEpicsIsolated(
  getWizardSchemaEpic,
  getProviderConfigEpic,
  runWizardActionEpic,
  savePsaIntegrationEpic,
  getPsaTileEpic,
  disablePsaIntegrationEpic
)
